import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Bot, X } from 'lucide-react'
import { formatUsPhone } from '@numeo/types-core'
import { SmsConversationSummary, SmsDriverThread, SmsThreadMessage } from '@numeo/types'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { fetchDriverSmsThread, fetchSmsThread, markSmsThreadRead } from '../services/boxTruckLoadsApi'
import { useConversationBot } from '../hooks/useConversationBot'
import { type BoxTruckSmsChatEntry, trackBoxTruckSmsChatOpened, trackBoxTruckSmsLineSwitched } from '../utils/analytics'
import { groupByDay } from '../utils/smsThread'
import { useSmsComposer } from '../hooks/useSmsComposer'
import { SmsThread } from './SmsThread'

/**
 * The selected driver's SMS conversation, beside the email workspace in the load drawer: the operator has
 * just picked who should take the load, and leaving the drawer to find them loses it.
 *
 * Mounted only once asked for, so picking driver after driver down the nearby list costs no requests.
 *
 * The thread is resolved BY DRIVER, server-side (`/sms/driver-thread/v1`) — the number the tenant would
 * text and the line the conversation already lives on. A page that guessed either would show a thread the
 * reply then left by a different door.
 */
export const BoxTruckDriverSmsPane = ({
    driverAccountId,
    driverName,
    entry,
    onClose,
}: {
    driverAccountId: string
    driverName: string | null
    /** Which button asked for it — reported with the open, never a second event (see `BoxTruckSmsChatEntry`). */
    entry: BoxTruckSmsChatEntry
    onClose: () => void
}) => {
    const { showSnackbar } = useSnackbar()
    const [thread, setThread] = useState<SmsDriverThread>({ counterpart: null, line: null, messages: [], lines: [], canReply: true, replyBlock: null })

    /** Why the composer is closed, said per case: one sentence for all three would be untrue twice. */
    const blocked: Record<NonNullable<SmsDriverThread['replyBlock']>, string> = {
        other_driver: 'Filed under another driver — read-only here',
        unattributed: 'This number is not matched to a driver yet, so Numeo cannot text it back',
        line_unavailable: 'That conversation is not available — pick another line',
    }
    /**
     * The conversation the operator picked, when this driver has more than one.
     *
     * Null means "whichever the server chooses" — theirs, falling back to the newest — which is what
     * opening the panel should do. Set only by the switcher.
     */
    const [pickedLine, setPickedLine] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    /** The read failed. Kept apart from "no number": one is a fact about the driver, the other about
     *  us, and showing the first for the second tells the operator something untrue. */
    const [failed, setFailed] = useState(false)
    const bottomRef = useRef<HTMLDivElement | null>(null)
    /** The driver this pane is currently showing, read inside callbacks that outlive a selection. */
    const showing = useRef(driverAccountId)
    showing.current = driverAccountId

    /**
     * Read the thread. Nothing else — it is also the composer's `refresh`, and a send must not look
     * like an operator opening the panel.
     *
     * Answers the thread it read, or null when the read failed, so the caller that cares about the
     * difference can say so without this one deciding anything.
     */
    const load = useCallback(
        /**
         * @param silent a re-read after a send, not an open. It leaves the messages on screen —
         * swapping a conversation for skeletons on every reply is a flicker and a lost scroll — and
         * says so differently when it fails, because the send DID happen and reporting it as a
         * failure is what makes an operator send twice.
         */
        async (silent = false): Promise<SmsDriverThread | null> => {
            if (!silent) setLoading(true)
            setFailed(false)
            try {
                const next = await fetchDriverSmsThread(driverAccountId, pickedLine)
                // The operator may have moved on while this was in flight; a late answer must not
                // replace the thread they are now reading.
                if (showing.current !== driverAccountId) return null
                setThread(next)
                return next
            } catch (e) {
                // The server's own sentence, not a generic one: a refusal here says WHY — the driver
                // is outside the caller's groups, or no longer on the roster — and replacing that
                // with "could not load" leaves the operator with nothing to act on.
                console.error('[box-truck] could not load the driver SMS thread', e)
                if (showing.current === driverAccountId) {
                    if (!silent) setFailed(true)
                    const message = silent
                        ? 'Sent — but the conversation could not be refreshed'
                        : getUserErrorMessage(e) || 'Could not load this driver’s messages'
                    showSnackbar(SnackbarType.WARNING, message)
                }
                return null
            } finally {
                if (!silent && showing.current === driverAccountId) setLoading(false)
            }
        },
        [driverAccountId, pickedLine, showSnackbar]
    )

    /** The driver whose open has already been counted, so switching lines does not count another. */
    const countedOpenFor = useRef<string | null>(null)

    /**
     * Read the thread on mount, and again when the operator switches to another of this driver's
     * conversations.
     *
     * Two things ride along, and they are deliberately not the same thing. Whatever thread ends up on
     * screen is being READ, so its badge is cleared — switching to a colleague's conversation counts.
     * The OPEN is counted once per driver: `load` is also the composer's refresh, and a send must not
     * look like an operator opening the panel, nor must picking a second line.
     */
    useEffect(() => {
        void (async () => {
            const opened = await load()
            if (showing.current !== driverAccountId) return
            if (opened?.counterpart && opened.line) {
                void markSmsThreadRead(opened.counterpart, opened.line).catch((e) =>
                    console.error('[box-truck] could not clear the SMS badge from the load drawer', e)
                )
            }
            if (countedOpenFor.current === driverAccountId) return
            countedOpenFor.current = driverAccountId
            trackBoxTruckSmsChatOpened(!opened ? 'failed' : !opened.counterpart ? 'no_number' : opened.line ? 'thread' : 'no_thread', entry)
        })()
    }, [load, driverAccountId, entry])

    // Live arrivals, off the same event the SMS page listens to — a driver answering while the drawer
    // is open belongs on screen, not behind a refresh.
    useEffect(() => {
        const onReceived = (event: Event) => {
            const detail = (event as CustomEvent<{ counterpart?: string; line?: string; message: SmsThreadMessage }>).detail
            if (!detail?.counterpart || detail.counterpart !== thread.counterpart) return
            // The LINE as well. A tenant can have several Quo numbers, and the same driver on two of
            // them is two conversations: appending a line-B message here would show it under line A
            // and send the reply out from A — the door this panel exists to keep shut. Before anyone
            // has written there is no line yet, and this message is what establishes it.
            if (thread.line && detail.line !== thread.line) return
            setThread((prev) =>
                prev.messages.some((m) => m.id === detail.message.id)
                    ? prev
                    : { ...prev, line: prev.line ?? detail.line ?? null, messages: [...prev.messages, detail.message] }
            )
            if (detail.line) {
                void markSmsThreadRead(detail.counterpart, detail.line).catch((e) =>
                    console.error('[box-truck] could not clear the SMS badge on a live arrival', e)
                )
            }
        }
        window.addEventListener('box-truck:sms-received', onReceived)
        return () => window.removeEventListener('box-truck:sms-received', onReceived)
    }, [thread.counterpart, thread.line])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ block: 'end' })
    }, [thread.messages])

    const grouped = useMemo(() => groupByDay(thread.messages), [thread.messages])

    /**
     * What `SmsThread` renders its header and composer from. Built here rather than fetched: the page
     * has a conversation list to take one from and the drawer does not, and the two fields that matter
     * to a reply — the number and the line — are exactly what the server just resolved.
     */
    const conversation: SmsConversationSummary | null = thread.counterpart
        ? {
              counterpart: thread.counterpart,
              line: thread.line ?? '',
              linePhoneNumberId: null,
              driverAccountId,
              driverId: null,
              name: driverName,
              lastMessage: '',
              lastDirection: 'out',
              // The drawer renders no preview line, so nothing here reads this one.
              lastSource: null,
              lastAt: new Date().toISOString(),
              unread: 0,
          }
        : null

    /**
     * The composer, shared with the SMS page: same four outcomes, same analytics, same handling of a
     * send that may have gone out. What is this surface's own is the thread it writes into and what
     * "still on screen" means here.
     */
    const { draft, setDraft, sending, send } = useSmsComposer({
        surface: 'load_drawer',
        // Whose thread this reply goes into, for the send event.
        mine: thread.lines.find((l) => l.line === thread.line)?.mine ?? true,
        thread: thread.counterpart ? { driverAccountId, counterpart: thread.counterpart, line: thread.line } : null,
        // The driver AND the line they picked: switching to a colleague's conversation must not carry
        // a half-typed message into it, where sending would put this operator's words out from the
        // colleague's number. Not `thread.line`, which the server fills in on open — that would wipe
        // a draft typed while the panel was still loading.
        threadKey: `${driverAccountId}|${pickedLine ?? ''}`,
        stillOpen: () => showing.current === driverAccountId,
        /**
         * Re-read after a send: the stored row carries the id, the status and the line, and
         * `unconfirmed` is a status only the server knows.
         *
         * Only the MESSAGES, through the thread endpoint. Sending changes nothing about which lines
         * this driver has conversations on, and the driver endpoint pays for that summary — a pass
         * over the number's messages inside the window — which would then be spent again on every
         * reply. Falls back to a full read only when there is no thread yet, because the first
         * message is exactly what creates one.
         */
        refresh: async () => {
            if (!thread.counterpart || !thread.line) {
                await load(true)
                return
            }
            try {
                const messages = await fetchSmsThread(thread.counterpart, thread.line)
                if (showing.current === driverAccountId) setThread((prev) => ({ ...prev, messages }))
            } catch (e) {
                console.error('[box-truck] could not refresh the driver SMS thread', e)
                if (showing.current === driverAccountId) showSnackbar(SnackbarType.WARNING, 'Sent — but the conversation could not be refreshed')
            }
        },
    })

    /**
     * The load bot in this conversation — the SAME hook the SMS page's bar uses.
     *
     * Written here as well, the two drifted on sight: two spellings of the paused state, two guards, two
     * error paths. What a driver's bot is doing must not depend on which screen you read it from (P6).
     *
     * The thread read already carries the state, so it is adopted rather than fetched again; after that
     * the hook owns it, which is what keeps the switch's answer and the label in step.
     */
    const bot = useConversationBot(driverAccountId, 'load_drawer')
    const { adopt: adoptBot } = bot
    useEffect(() => adoptBot(thread.bot), [thread.bot, adoptBot])

    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-surface">
            <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">SMS</span>
                {/* The load bot in THIS conversation (ADR-057). Shown only when the server said what state
                    it is in — the thread is the point of this panel, and a settings read that failed must
                    not draw a switch whose position would be a guess. Off by an admin or by the driver's own
                    STOP is not this operator's to undo, so the box is disabled and says who did it. */}
                {bot.bot && (
                    <label
                        className={`ml-2 mr-auto inline-flex items-center gap-1.5 text-[11px] ${bot.off ? 'text-foreground-muted' : 'text-foreground'}`}
                        title={bot.hint}
                    >
                        <input
                            type="checkbox"
                            checked={bot.bot.on}
                            disabled={bot.saving || !bot.mine}
                            onChange={(event) => void bot.toggle(event.target.checked)}
                            className="h-3.5 w-3.5 rounded border-border accent-accent"
                        />
                        <Bot size={13} />
                        {bot.label}
                    </label>
                )}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close SMS chat"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                >
                    <X size={16} />
                </button>
            </div>
            {/* Other conversations with the same driver — one per company line, because a thread is a
                line AND a number. Shown only when there is a choice to make; the one that opened is
                this operator's own where they have one. */}
            {thread.lines.length > 1 && (
                <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-border bg-surface px-3 py-1.5 text-[11px]">
                    <span className="text-foreground-muted">Also on</span>
                    {thread.lines.map((option) => (
                        <button
                            key={option.line}
                            type="button"
                            onClick={() => {
                                trackBoxTruckSmsLineSwitched(option.mine)
                                setPickedLine(option.line)
                            }}
                            title={option.mine ? 'Your conversation with this driver' : 'A teammate’s conversation with this driver'}
                            className={`rounded-md border px-1.5 py-0.5 transition-colors ${
                                option.line === thread.line
                                    ? 'border-accent bg-accent-soft text-accent'
                                    : 'border-border bg-background text-foreground-muted hover:bg-default hover:text-foreground'
                            }`}
                        >
                            {formatUsPhone(option.line)}
                            {option.mine && <span className="ml-1 opacity-70">you</span>}
                        </button>
                    ))}
                </div>
            )}
            {!loading && failed ? (
                // The snackbar that said why has gone by now; the body is what stays on screen.
                <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-foreground-muted">
                    <span>Could not load this conversation.</span>
                    <button
                        type="button"
                        onClick={() => void load()}
                        className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold text-foreground transition-colors hover:bg-default"
                    >
                        Try again
                    </button>
                </div>
            ) : !loading && !thread.counterpart ? (
                // Not an error and not an empty thread: there is no number to write to, so the composer
                // would be a promise we cannot keep.
                <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-foreground-muted">
                    We have no textable number for {driverName ?? 'this driver'}.
                </div>
            ) : (
                <div className="flex min-h-0 flex-1 flex-col">
                    <SmsThread
                        conversation={conversation}
                        // Filed under another driver — a shared or reassigned number. The server
                        // refuses a reply that names this one, so the composer says so instead of
                        // taking words nobody will send.
                        readOnly={thread.canReply || !thread.replyBlock ? undefined : { reason: blocked[thread.replyBlock] }}
                        // This pane has its own switch in its header — see `botBar` on SmsThread.
                        botBar={false}
                        messages={grouped}
                        loading={loading}
                        draft={draft}
                        sending={sending}
                        bottomRef={bottomRef}
                        onDraft={setDraft}
                        onSend={() => void send()}
                    />
                </div>
            )}
        </div>
    )
}
